/**
 * Precios AR: la tabla de tramos de una empresa (docs/ar-precios-tramos-plan.md).
 *
 * AR no cobra un porcentaje: cobra un precio fijo al mes según el tramo en el que
 * caen los euros gestionados del mes (06-reparto §5.5). Precios sin IVA.
 *
 * Un tramo es `{ from, to, price }`: se aplica si `from ≤ total < to`. `to: null`
 * = sin límite, y solo puede serlo el último. Los tramos empiezan en 0 y van
 * seguidos: el `from` de cada uno es el `to` del anterior. Así no hay huecos ni
 * solapes, y todo total cae en un tramo o por encima del último.
 *
 * Lo usan el backend (que es la puerta) y el editor del perfil, que avisa antes de
 * guardar. Las reglas viven solo aquí.
 */

const MAX_TIERS = 20;
const VAT_RATE = 0.21;

const isMoney = (n) => typeof n === 'number' && Number.isFinite(n) && n >= 0;
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Los errores de una tabla, en castellano. Vacío = válida.
 * @param {Array<{from:number,to:number|null,price:number}>} tiers
 * @returns {string[]}
 */
function validateTiers(tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return ['Hace falta al menos un tramo'];
  if (tiers.length > MAX_TIERS) return [`Como mucho ${MAX_TIERS} tramos`];

  const errors = [];
  tiers.forEach((t, i) => {
    const n = i + 1;
    const last = i === tiers.length - 1;
    if (!t || !isMoney(t.from)) errors.push(`Tramo ${n}: «desde» no es un importe válido`);
    if (!t || !isMoney(t.price)) errors.push(`Tramo ${n}: el precio no es un importe válido`);
    if (t && t.to === null) {
      if (!last) errors.push(`Tramo ${n}: solo el último puede no tener límite`);
    } else if (!t || !isMoney(t.to)) {
      errors.push(`Tramo ${n}: «hasta» no es un importe válido`);
    } else if (isMoney(t.from) && t.to <= t.from) {
      errors.push(`Tramo ${n}: «hasta» tiene que ser mayor que «desde»`);
    }
    if (i === 0 && t && t.from !== 0) errors.push('El primer tramo tiene que empezar en 0 €');
    if (i > 0 && t && tiers[i - 1] && t.from !== tiers[i - 1].to) {
      errors.push(`Tramo ${n}: tiene que empezar donde acaba el tramo ${i} (${tiers[i - 1].to} €)`);
    }
  });
  return errors;
}

/**
 * Qué se factura por un total gestionado.
 *
 * @returns {null | {
 *   tier_index: number|null, from, to, price, amount, vat, amount_with_vat,
 *   above_last: boolean,
 *   next: null | { from, price, remaining }
 * }}
 *   null si no hay tabla. `above_last` si el total pasa del último tramo (que
 *   tiene límite): no hay precio y `amount` es null.
 */
function priceFor(total, tiers) {
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  const value = Number(total) || 0;
  const index = tiers.findIndex((t) => value >= t.from && (t.to === null || value < t.to));

  if (index === -1) {
    const last = tiers[tiers.length - 1];
    return {
      tier_index: null,
      from: last.to,
      to: null,
      price: null,
      amount: null,
      vat: null,
      amount_with_vat: null,
      above_last: true,
      next: null,
    };
  }

  const tier = tiers[index];
  const following = tiers[index + 1];
  return {
    tier_index: index,
    from: tier.from,
    to: tier.to,
    price: tier.price,
    amount: round2(tier.price),
    vat: round2(tier.price * VAT_RATE),
    amount_with_vat: round2(tier.price * (1 + VAT_RATE)),
    above_last: false,
    next: following ? { from: following.from, price: following.price, remaining: round2(following.from - value) } : null,
  };
}

module.exports = { MAX_TIERS, VAT_RATE, validateTiers, priceFor };
